// aiMuse 剪辑数据接口客户端。
// 剪辑器不直连数据库，只带登录 JWT 调用 aiMuse 后端：
//   - 章节分镜视频清单 / 视频下载代理
//   - 章节时间轴草稿读写（带乐观锁）
import { getLlmSettings, saveLlmSettings } from './settings.js';
import * as opfs from './opfs.js';

/** 读取当前剪辑上下文；缺少 apiBase / token / 项目 / 章节时返回 null，调用方跳过同步。 */
export async function getContext() {
    const settings = await getLlmSettings();
    const apiBase = String(settings.apiBase || '').replace(/\/+$/, '');
    const accessToken = String(settings.accessToken || '');
    const museProjectId = String(settings.museProjectId || '');
    const museChapterId = String(settings.museChapterId || '');
    if (!apiBase || !accessToken || !museProjectId || !museChapterId) {
        return null;
    }
    try {
        const parsed = new URL(apiBase);
        if (!['http:', 'https:'].includes(parsed.protocol)) return null;
    } catch {
        return null;
    }
    return {
        ...settings,
        apiBase,
        accessToken,
        museProjectId,
        museChapterId,
        appVersion: String(settings.appVersion || ''),
        variant: settings.variant === 'enhance' ? 'enhance' : 'original',
    };
}

function authHeaders(context, json = false) {
    const headers = {
        Authorization: `Bearer ${context.accessToken}`,
        token: context.accessToken,
        'X-Muse-App-Version': context.appVersion || 'domestic',
    };
    if (json) {
        headers['Content-Type'] = 'application/json';
    }
    return headers;
}

async function requestJson(context, path, options = {}) {
    // 自动字幕 / 简繁转换等长请求可以传 timeoutMs，避免后端无响应时批量任务一直挂着。
    const timeoutMs = Number(options.timeoutMs) || 0;
    const controller = timeoutMs > 0 && typeof AbortController !== 'undefined'
        ? new AbortController()
        : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
    try {
        const response = await fetch(`${context.apiBase}${path}`, {
            method: options.method || 'GET',
            headers: {
                ...authHeaders(context, Boolean(options.json)),
                ...(options.headers || {}),
            },
            body: options.body,
            ...(controller ? { signal: controller.signal } : {}),
        });
        const text = await response.text();
        let body = {};
        try {
            body = text ? JSON.parse(text) : {};
        } catch {
            body = {};
        }
        if (!response.ok) {
            const error = new Error(body.msg || body.message || `请求失败（${response.status}）`);
            error.status = response.status;
            error.code = body.code;
            error.latestVersion = body?.data?.version;
            error.latestUpdatedAt = body?.data?.updatedAt;
            throw error;
        }
        return body.data !== undefined ? body.data : body;
    } catch (error) {
        if (controller?.signal.aborted) {
            const timeoutError = new Error(`请求超时（${Math.max(1, Math.round(timeoutMs / 1000))} 秒）`);
            timeoutError.code = 'REQUEST_TIMEOUT';
            throw timeoutError;
        }
        throw error;
    } finally {
        if (timer) clearTimeout(timer);
    }
}

/** 读取项目级共享剪辑草稿（所有章节共用；后端 chapter_id = 0）。 */
export async function fetchDraft() {
    const context = await getContext();
    if (!context) {
        return { exists: false, protocol: null, version: 0, updatedAt: null, name: '' };
    }
    return requestJson(
        context,
        `/nle/projects/${encodeURIComponent(context.museProjectId)}/edit`,
    );
}

/**
 * 保存剪辑草稿。
 * 409 时用服务端返回的最新版本重试一次，避免自动保存把用户改动丢掉。
 */
export async function saveDraft({ protocol, baseVersion, name }) {
    const context = await getContext();
    if (!context) {
        return { skipped: true };
    }
    const path = `/nle/projects/${encodeURIComponent(context.museProjectId)}/edit`;
    const requestBody = {
        protocol,
        baseVersion: Number(baseVersion) || 0,
        name: String(name || ''),
    };
    try {
        return await requestJson(context, path, {
            method: 'PUT',
            json: true,
            body: JSON.stringify(requestBody),
        });
    } catch (error) {
        if (error.status === 409 && error.latestVersion !== null && error.latestVersion !== undefined) {
            return requestJson(context, path, {
                method: 'PUT',
                json: true,
                body: JSON.stringify({ ...requestBody, baseVersion: Number(error.latestVersion) || 0 }),
            });
        }
        throw error;
    }
}

/** 查询本集分镜视频清单。 */
export async function fetchChapterMaterials() {
    const context = await getContext();
    if (!context) {
        return { materials: [] };
    }
    const query = `?variant=${encodeURIComponent(context.variant)}`;
    return requestJson(
        context,
        `/nle/projects/${encodeURIComponent(context.museProjectId)}/chapters/${encodeURIComponent(context.museChapterId)}/materials${query}`,
    );
}

/**
 * 查询整个项目所有章节的分镜视频清单。
 * 共享时间线可能引用多个章节的素材，新设备恢复文件时必须拿到全量清单。
 */
export async function fetchProjectMaterials() {
    const context = await getContext();
    if (!context) {
        return { materials: [] };
    }
    const query = `?variant=${encodeURIComponent(context.variant)}`;
    return requestJson(
        context,
        `/nle/projects/${encodeURIComponent(context.museProjectId)}/materials${query}`,
    );
}

/**
 * 只读取分镜视频的总字节数（Range: bytes=0-0），用于判断本地素材是否还是同一条视频。
 * 老工程没有 videoUrl 基线时用它做低成本比对，避免首轮同步把整集视频全部重下。
 */
export async function fetchMaterialSize(proxyUrl) {
    const context = await getContext();
    if (!context) return 0;
    const path = String(proxyUrl || '').startsWith('/') ? proxyUrl : `/${proxyUrl}`;
    try {
        const response = await fetch(`${context.apiBase}${path}`, {
            method: 'GET',
            headers: { ...authHeaders(context, false), Range: 'bytes=0-0' },
            cache: 'no-store',
        });
        try {
            // 跨域默认只暴露简单响应头；只有服务端配置了 Content-Range 才能拿到总大小。
            const range = String(response.headers.get('content-range') || '');
            const total = Number(range.split('/')[1]);
            if (Number.isFinite(total) && total > 0) return total;
            // 206 的 content-length 只是本次区间长度（这里是 1），不能当总大小用。
            if (response.status === 200) {
                const length = Number(response.headers.get('content-length'));
                return Number.isFinite(length) && length > 0 ? length : 0;
            }
            return 0;
        } finally {
            // 只关心响应头；服务端忽略 Range 时立即取消，避免把整段视频下载完。
            response.body?.cancel?.().catch(() => {});
        }
    } catch {
        return 0;
    }
}

/** 查询本集各分镜的历史抽卡视频（按分镜分组）。 */
export async function fetchChapterMaterialHistories() {
    const context = await getContext();
    if (!context) {
        return { storyboards: [] };
    }
    return requestJson(
        context,
        `/nle/projects/${encodeURIComponent(context.museProjectId)}`
        + `/chapters/${encodeURIComponent(context.museChapterId)}/material-histories`,
    );
}

/** 查询整个项目的历史抽卡视频，用于共享时间线跨章节恢复素材。 */
export async function fetchProjectMaterialHistories() {
    const context = await getContext();
    if (!context) {
        return { storyboards: [] };
    }
    return requestJson(
        context,
        `/nle/projects/${encodeURIComponent(context.museProjectId)}/material-histories`,
    );
}

/** 经 aiMuse 后端代理下载分镜视频 Blob。 */
export async function fetchMaterialBlob(proxyUrl) {
    const context = await getContext();
    if (!context) {
        throw new Error('缺少剪辑上下文，无法下载分镜视频');
    }
    const path = String(proxyUrl || '').startsWith('/') ? proxyUrl : `/${proxyUrl}`;
    // 分镜重抽后代理地址不变，加时间戳绕过浏览器对旧视频的缓存。
    const separator = path.includes('?') ? '&' : '?';
    const response = await fetch(`${context.apiBase}${path}${separator}_t=${Date.now()}`, {
        headers: authHeaders(context, false),
        cache: 'no-store',
    });
    if (!response.ok) {
        let message = `素材下载失败（${response.status}）`;
        try {
            const body = await response.json();
            message = body.msg || body.message || message;
        } catch {
            // 代理返回非 JSON 时沿用状态码提示
        }
        const error = new Error(message);
        error.status = response.status;
        throw error;
    }
    return response.blob();
}

// 串行化上下文写入：自动导入与草稿保存可能同时更新 importedStoryboardIds / museDraftVersion，
// 串行执行避免后写覆盖前写。
let contextUpdateQueue = Promise.resolve();

/**
 * 修复草稿里缺失的 OPFS 视频文件。
 * 场景：新设备/新浏览器打开服务端草稿，协议引用的视频路径还不存在。
 * 按协议里的素材名称匹配当前章节素材，下载后写回原路径，时间轴引用无需改写。
 */
export async function restoreMaterialFiles(protocol, onProgress, providedMaterials) {
    const videos = Array.isArray(protocol?.materials?.videos) ? protocol.materials.videos : [];
    if (!videos.length) {
        return { total: 0, restored: 0, missing: 0 };
    }
    const context = await getContext();
    if (!context) {
        return { total: videos.length, restored: 0, missing: videos.length, skipped: true };
    }

    let apiMaterials = Array.isArray(providedMaterials) ? providedMaterials : null;
    if (!apiMaterials) {
        let payload;
        try {
            // 共享时间线可能包含任意章节的素材，默认按全项目清单恢复。
            payload = await fetchProjectMaterials();
        } catch (error) {
            console.warn('读取项目素材清单失败，跳过草稿素材修复', error);
            return { total: videos.length, restored: 0, missing: videos.length, error: error.message };
        }
        apiMaterials = Array.isArray(payload?.materials) ? payload.materials : [];
    }
    // 时间轴里可能用到了「历史素材」，按素材名一并纳入恢复清单。
    let historyMaterials = [];
    try {
        const historyPayload = await fetchProjectMaterialHistories();
        historyMaterials = (Array.isArray(historyPayload?.storyboards) ? historyPayload.storyboards : [])
            .flatMap(group => (Array.isArray(group?.history) ? group.history : []))
            .map(item => ({ name: item.name, proxyUrl: item.proxyUrl }));
    } catch (error) {
        console.warn('读取项目历史素材清单失败，跳过历史素材修复', error);
    }
    apiMaterials = [...apiMaterials, ...historyMaterials];
    if (!apiMaterials.length) {
        return { total: videos.length, restored: 0, missing: videos.length };
    }

    let restored = 0;
    let missing = 0;
    for (const [index, material] of videos.entries()) {
        if (!material?.path || !material?.name) {
            continue;
        }
        const existing = await opfs.getFile(material.path).catch(() => null);
        if (existing) {
            continue;
        }
        const source = apiMaterials.find(item => item.name === material.name);
        if (!source) {
            missing += 1;
            continue;
        }
        onProgress?.(index + 1, videos.length, material);
        try {
            const blob = await fetchMaterialBlob(source.proxyUrl);
            await opfs.putFile(material.path, blob);
            restored += 1;
        } catch (error) {
            missing += 1;
            console.warn(`恢复剪辑素材失败：${material.name}`, error);
        }
    }
    return { total: videos.length, restored, missing };
}

/** 上传导出的 MP4 到 aiMuse 后端（由后端转存 TOS），XHR 用于拿上传进度。 */
export async function uploadExport({ blob, fileName, durationMs, width, height, fps, onProgress }) {
    const context = await getContext();
    if (!context) {
        throw new Error('缺少剪辑上下文，无法上传导出文件');
    }
    const form = new FormData();
    form.append('file', blob, fileName);
    form.append('fileName', fileName);
    form.append('durationMs', String(Math.max(0, Math.round(Number(durationMs) || 0))));
    form.append('width', String(Math.max(0, Math.round(Number(width) || 0))));
    form.append('height', String(Math.max(0, Math.round(Number(height) || 0))));
    form.append('fps', String(Number(fps) || 0));

    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(
            'POST',
            `${context.apiBase}/nle/projects/${encodeURIComponent(context.museProjectId)}/exports`,
        );
        xhr.setRequestHeader('Authorization', `Bearer ${context.accessToken}`);
        xhr.setRequestHeader('token', context.accessToken);
        xhr.setRequestHeader('X-Muse-App-Version', context.appVersion || 'domestic');
        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable && typeof onProgress === 'function') {
                onProgress(event.loaded / event.total);
            }
        };
        xhr.onload = () => {
            let body = {};
            try { body = JSON.parse(xhr.responseText || '{}'); } catch { body = {}; }
            if (xhr.status >= 200 && xhr.status < 300 && body.data) {
                resolve(body.data);
            } else {
                reject(new Error(body.msg || body.message || `上传失败（${xhr.status}）`));
            }
        };
        xhr.onerror = () => reject(new Error('上传失败，请检查网络'));
        xhr.onabort = () => reject(new Error('上传已取消'));
        xhr.send(form);
    });
}

/** 查询项目级导出历史（包含共享时间线与历史章节导出记录）。 */
export async function fetchExports() {
    const context = await getContext();
    if (!context) {
        return [];
    }
    return requestJson(
        context,
        `/nle/projects/${encodeURIComponent(context.museProjectId)}/exports`,
    );
}

/**
 * 提交分镜视频的语音转字幕任务。
 * 命中已完成缓存时直接返回字幕；否则返回 MediaKit 任务 ID，由调用方轮询。
 */
export async function submitMaterialTranscription({ storyboardId, variant, language, force }) {
    const context = await getContext();
    if (!context) {
        throw new Error('缺少剪辑上下文，无法识别字幕');
    }
    return requestJson(
        context,
        `/nle/projects/${encodeURIComponent(context.museProjectId)}`
        + `/chapters/${encodeURIComponent(context.museChapterId)}`
        + `/materials/${encodeURIComponent(storyboardId)}/transcriptions`,
        {
            method: 'POST',
            json: true,
            body: JSON.stringify({
                variant: variant || context.variant || 'original',
                language: language || 'auto',
                force: Boolean(force),
            }),
            timeoutMs: 60_000,
        },
    );
}

/**
 * 提交外部素材音频的语音转字幕任务。
 * 前端已把素材抽成 16kHz 单声道 WAV，这里用 multipart 直接上传。
 */
export async function submitAudioTranscription({ audioBlob, fileName, language, durationMs, force }) {
    const context = await getContext();
    if (!context) {
        throw new Error('缺少剪辑上下文，无法识别字幕');
    }
    const form = new FormData();
    form.append('audio', audioBlob, String(fileName || 'material.wav'));
    form.append('language', language || 'auto');
    form.append('durationMs', String(Math.max(0, Math.round(Number(durationMs) || 0))));
    form.append('force', force ? '1' : '0');
    // 上传抽好的 WAV 可能较大，给 60 秒超时；无响应时让上层按「无字幕」继续。
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 60_000);
    try {
        const response = await fetch(
            `${context.apiBase}/nle/projects/${encodeURIComponent(context.museProjectId)}`
            + `/chapters/${encodeURIComponent(context.museChapterId)}/transcriptions`,
            {
                method: 'POST',
                headers: authHeaders(context, false),
                body: form,
                signal: controller.signal,
            },
        );
        const text = await response.text();
        let body = {};
        try {
            body = text ? JSON.parse(text) : {};
        } catch {
            body = {};
        }
        if (!response.ok) {
            throw new Error(body.msg || body.message || `请求失败（${response.status}）`);
        }
        return body.data !== undefined ? body.data : body;
    } catch (error) {
        if (controller.signal.aborted) {
            throw new Error('字幕任务提交超时（60 秒）');
        }
        throw error;
    } finally {
        clearTimeout(timer);
    }
}

/**
 * 自动剪辑简繁转换。
 * 一次提交整批字幕，服务端用本地 OpenCC 词典转换，不经过大模型。
 */
export async function convertScript({ texts, to = 't' }) {
    const context = await getContext();
    if (!context) {
        throw new Error('缺少剪辑上下文，无法转换字幕');
    }
    const list = Array.isArray(texts) ? texts.map(item => String(item ?? '')) : [];
    if (!list.length) {
        return [];
    }
    const result = await requestJson(
        context,
        '/nle/auto-cut/script/convert',
        {
            method: 'POST',
            json: true,
            body: JSON.stringify({ texts: list, to }),
            timeoutMs: 30_000,
        },
    );
    return Array.isArray(result?.texts) ? result.texts : list;
}

/** 自动剪辑模板列表：内置 + 当前用户自建。 */
export async function fetchAutoCutTemplates() {
    const context = await getContext();
    if (!context) {
        return { templates: [] };
    }
    return requestJson(context, '/nle/auto-cut/templates');
}

/** 新建自动剪辑模板。 */
export async function saveAutoCutTemplate({ name, category, config }) {
    const context = await getContext();
    if (!context) {
        throw new Error('缺少剪辑上下文，无法保存模板');
    }
    return requestJson(context, '/nle/auto-cut/templates', {
        method: 'POST',
        json: true,
        body: JSON.stringify({ name, category, config }),
    });
}

/** 更新自动剪辑模板。 */
export async function updateAutoCutTemplate({ id, name, category, config }) {
    const context = await getContext();
    if (!context) {
        throw new Error('缺少剪辑上下文，无法更新模板');
    }
    return requestJson(context, `/nle/auto-cut/templates/${encodeURIComponent(id)}`, {
        method: 'PUT',
        json: true,
        body: JSON.stringify({ name, category, config }),
    });
}

/** 删除自动剪辑模板（软删）。 */
export async function deleteAutoCutTemplate(id) {
    const context = await getContext();
    if (!context) {
        throw new Error('缺少剪辑上下文，无法删除模板');
    }
    return requestJson(context, `/nle/auto-cut/templates/${encodeURIComponent(id)}`, {
        method: 'DELETE',
    });
}

/** 尾帧视频库列表。 */
export async function fetchTailVideos() {
    const context = await getContext();
    if (!context) {
        return { tails: [] };
    }
    return requestJson(context, '/nle/auto-cut/tails');
}

/** 上传尾帧视频；时长/宽高由调用方读取后传入。 */
export async function uploadTailVideo({ file, name, durationMs, width, height, onProgress }) {
    const context = await getContext();
    if (!context) {
        throw new Error('缺少剪辑上下文，无法上传尾帧');
    }
    const form = new FormData();
    form.append('file', file, file.name || 'tail.mp4');
    form.append('name', String(name || file.name || '尾帧视频'));
    form.append('durationMs', String(Math.max(0, Math.round(Number(durationMs) || 0))));
    form.append('width', String(Math.max(0, Math.round(Number(width) || 0))));
    form.append('height', String(Math.max(0, Math.round(Number(height) || 0))));
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${context.apiBase}/nle/auto-cut/tails/upload`);
        xhr.setRequestHeader('Authorization', `Bearer ${context.accessToken}`);
        xhr.setRequestHeader('token', context.accessToken);
        xhr.setRequestHeader('X-Muse-App-Version', context.appVersion || 'domestic');
        xhr.upload.onprogress = event => {
            if (event.lengthComputable && typeof onProgress === 'function') {
                onProgress(event.loaded / event.total);
            }
        };
        xhr.onload = () => {
            let body = {};
            try { body = JSON.parse(xhr.responseText || '{}'); } catch { body = {}; }
            if (xhr.status >= 200 && xhr.status < 300 && body.data) resolve(body.data);
            else reject(new Error(body.msg || body.message || `上传失败（${xhr.status}）`));
        };
        xhr.onerror = () => reject(new Error('上传失败，请检查网络'));
        xhr.onabort = () => reject(new Error('上传已取消'));
        xhr.send(form);
    });
}

/** 删除尾帧。 */
export async function deleteTailVideo(id) {
    const context = await getContext();
    if (!context) {
        throw new Error('缺少剪辑上下文，无法删除尾帧');
    }
    return requestJson(context, `/nle/auto-cut/tails/${encodeURIComponent(id)}`, {
        method: 'DELETE',
    });
}

/** 下载尾帧视频 Blob（经 aiMuse 后端代理）。 */
export async function fetchTailVideoBlob(id) {
    const context = await getContext();
    if (!context) {
        throw new Error('缺少剪辑上下文，无法下载尾帧');
    }
    const response = await fetch(`${context.apiBase}/nle/auto-cut/tails/${encodeURIComponent(id)}/file`, {
        headers: authHeaders(context, false),
        cache: 'no-store',
    });
    if (!response.ok) {
        let message = `尾帧下载失败（${response.status}）`;
        try {
            const body = await response.json();
            message = body.msg || body.message || message;
        } catch { /* 非 JSON 错误沿用状态码 */ }
        throw new Error(message);
    }
    return response.blob();
}

/** 公共 BGM 库列表（所有项目共用）。 */
export async function fetchBgmTracks() {
    const context = await getContext();
    if (!context) {
        return { tracks: [] };
    }
    return requestJson(context, '/nle/auto-cut/bgm');
}

/** 上传公共 BGM；时长由调用方读取后传入。 */
export async function uploadBgmTrack({ file, name, durationMs, onProgress }) {
    const context = await getContext();
    if (!context) {
        throw new Error('缺少剪辑上下文，无法上传 BGM');
    }
    const form = new FormData();
    form.append('file', file, file.name || 'bgm.mp3');
    form.append('name', String(name || file.name || 'BGM'));
    form.append('durationMs', String(Math.max(0, Math.round(Number(durationMs) || 0))));
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${context.apiBase}/nle/auto-cut/bgm/upload`);
        xhr.setRequestHeader('Authorization', `Bearer ${context.accessToken}`);
        xhr.setRequestHeader('token', context.accessToken);
        xhr.setRequestHeader('X-Muse-App-Version', context.appVersion || 'domestic');
        xhr.upload.onprogress = event => {
            if (event.lengthComputable && typeof onProgress === 'function') {
                onProgress(event.loaded / event.total);
            }
        };
        xhr.onload = () => {
            let body = {};
            try { body = JSON.parse(xhr.responseText || '{}'); } catch { body = {}; }
            if (xhr.status >= 200 && xhr.status < 300 && body.data) resolve(body.data);
            else reject(new Error(body.msg || body.message || `BGM 上传失败（${xhr.status}）`));
        };
        xhr.onerror = () => reject(new Error('BGM 上传失败，请检查网络'));
        xhr.onabort = () => reject(new Error('BGM 上传已取消'));
        xhr.send(form);
    });
}

/** 删除公共 BGM。 */
export async function deleteBgmTrack(id) {
    const context = await getContext();
    if (!context) {
        throw new Error('缺少剪辑上下文，无法删除 BGM');
    }
    return requestJson(context, `/nle/auto-cut/bgm/${encodeURIComponent(id)}`, {
        method: 'DELETE',
    });
}

/** 下载公共 BGM Blob（经 aiMuse 后端代理）。 */
export async function fetchBgmBlob(id) {
    const context = await getContext();
    if (!context) {
        throw new Error('缺少剪辑上下文，无法下载 BGM');
    }
    const response = await fetch(`${context.apiBase}/nle/auto-cut/bgm/${encodeURIComponent(id)}/file`, {
        headers: authHeaders(context, false),
        cache: 'no-store',
    });
    if (!response.ok) {
        let message = `BGM 下载失败（${response.status}）`;
        try {
            const body = await response.json();
            message = body.msg || body.message || message;
        } catch { /* 非 JSON 错误沿用状态码 */ }
        throw new Error(message);
    }
    return response.blob();
}

/** 创建自动剪辑运行记录。 */
export async function createAutoCutRun({ mode, name, totalCount, outputDirName, outputRoot, config }) {
    const context = await getContext();
    if (!context) return null;
    return requestJson(context, '/nle/auto-cut/runs', {
        method: 'POST',
        json: true,
        body: JSON.stringify({
            projectId: context.museProjectId,
            chapterId: context.museChapterId,
            mode,
            name,
            totalCount,
            outputDirName,
            outputRoot,
            config,
        }),
        timeoutMs: 15_000,
    });
}

/** 更新自动剪辑运行记录。 */
export async function updateAutoCutRun({
    id,
    status,
    successCount,
    failedCount,
    outputDirName,
    outputRoot,
    results,
    errorMessage,
    finished,
}) {
    const context = await getContext();
    if (!context || !id) return null;
    return requestJson(context, `/nle/auto-cut/runs/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        json: true,
        body: JSON.stringify({
            status,
            successCount,
            failedCount,
            outputDirName,
            outputRoot,
            results,
            errorMessage,
            finished,
        }),
        timeoutMs: 15_000,
    });
}

/** 自动剪辑运行记录列表（当前用户）。 */
export async function fetchAutoCutRuns({ limit = 20 } = {}) {
    const context = await getContext();
    if (!context) return { runs: [] };
    const query = new URLSearchParams({
        projectId: String(context.museProjectId || ''),
        limit: String(limit),
    });
    return requestJson(context, `/nle/auto-cut/runs?${query.toString()}`, { timeoutMs: 15_000 });
}

/** 自动剪辑运行详情。 */
export async function fetchAutoCutRunDetail(id) {
    const context = await getContext();
    if (!context) throw new Error('缺少剪辑上下文');
    return requestJson(context, `/nle/auto-cut/runs/${encodeURIComponent(id)}`, { timeoutMs: 15_000 });
}

/** 查询语音转字幕任务；由剪辑器定时轮询，直到 completed / failed。 */
export async function queryMaterialTranscription(taskId) {
    const context = await getContext();
    if (!context) {
        throw new Error('缺少剪辑上下文，无法查询字幕任务');
    }
    return requestJson(
        context,
        `/nle/projects/${encodeURIComponent(context.museProjectId)}`
        + `/chapters/${encodeURIComponent(context.museChapterId)}`
        + `/transcriptions/${encodeURIComponent(taskId)}`,
        { timeoutMs: 30_000 },
    );
}

/**
 * 合并写入某集的分镜素材同步基线。
 * 按 storyboardId 覆盖，不影响其它章节记录；与草稿版本、导入 ID 共用串行队列，避免并发覆盖。
 */
export async function mergeStoryboardMaterialVersions(chapterKey, entries) {
    if (!chapterKey || !entries || typeof entries !== 'object') {
        return undefined;
    }
    contextUpdateQueue = contextUpdateQueue
        .catch(() => {})
        .then(async () => {
            const current = await getLlmSettings();
            const versions = { ...(current.storyboardMaterialVersions || {}) };
            versions[chapterKey] = { ...(versions[chapterKey] || {}), ...entries };
            return saveLlmSettings({ ...current, storyboardMaterialVersions: versions });
        });
    return contextUpdateQueue;
}

/** 合并写入剪辑上下文（项目/章节/API 地址/草稿版本等）。 */
export async function updateContext(fields) {
    contextUpdateQueue = contextUpdateQueue
        .catch(() => {})
        .then(async () => {
            const current = await getLlmSettings();
            return saveLlmSettings({ ...current, ...fields });
        });
    return contextUpdateQueue;
}

export default {
    getContext,
    fetchDraft,
    saveDraft,
    fetchChapterMaterials,
    fetchProjectMaterials,
    fetchChapterMaterialHistories,
    fetchProjectMaterialHistories,
    fetchMaterialBlob,
    fetchMaterialSize,
    restoreMaterialFiles,
    uploadExport,
    fetchExports,
    submitMaterialTranscription,
    submitAudioTranscription,
    convertScript,
    fetchAutoCutTemplates,
    saveAutoCutTemplate,
    updateAutoCutTemplate,
    deleteAutoCutTemplate,
    fetchTailVideos,
    uploadTailVideo,
    deleteTailVideo,
    fetchTailVideoBlob,
    fetchBgmTracks,
    uploadBgmTrack,
    deleteBgmTrack,
    fetchBgmBlob,
    createAutoCutRun,
    updateAutoCutRun,
    fetchAutoCutRuns,
    fetchAutoCutRunDetail,
    queryMaterialTranscription,
    mergeStoryboardMaterialVersions,
    updateContext,
};
